import React from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Stack,
  alpha,
} from "@mui/material";
import {
  School,
  LocationOn,
  Email,
  Phone,
  Person,
  Visibility,
  Flag,
  EmojiEvents,
  CheckCircle,
  CampaignOutlined,
} from "@mui/icons-material";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { getSchool } from "../redux/actions/schoolActions";
import AppCircularProgress from "../components/common/AppCircularProgress";
import PageError from "../components/common/PageError";

const HomePage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const { loadingSchool, errorLoadingSchool, school } = useAppSelector(
    (state) => state.school,
  );

  const activeBranches =
    school?.branches?.filter((branch) => branch.branchStatus === "ACTIVE") ||
    [];

  if (loadingSchool) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <AppCircularProgress />
      </Box>
    );
  }

  if (errorLoadingSchool) {
    return (
      <Box sx={{ p: 4 }}>
        <PageError
          error={errorLoadingSchool}
          onRetry={() => dispatch(getSchool())}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 8 }}>
      {school && (
        <>
          {/* HERO SECTION */}
          <Paper
            elevation={0}
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              py: 8,
              mb: 4,
              borderRadius: 0,
            }}
          >
            <Container maxWidth="lg">
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "8fr 4fr" },
                  gap: 4,
                  alignItems: "center",
                }}
              >
                <Box>
                  <Stack
                    direction="row"
                    spacing={3}
                    sx={{ alignItems: "center", mb: 3 }}
                  >
                    <Avatar
                      src={school.logoUrl}
                      sx={{
                        width: 80,
                        height: 80,
                        border: "3px solid white",
                      }}
                    >
                      <School sx={{ fontSize: 40 }} />
                    </Avatar>
                    <Box>
                      <Typography
                        variant="h2"
                        component="h1"
                        sx={{ fontWeight: "bold", mb: 0.5 }}
                      >
                        {school.name}
                      </Typography>
                      <Typography variant="h6" sx={{ opacity: 0.9 }}>
                        Registration: {school.registrationNumber}
                      </Typography>
                    </Box>
                  </Stack>
                  <Typography
                    variant="h4"
                    sx={{ fontStyle: "italic", mb: 2, opacity: 0.95 }}
                  >
                    "{school.motto}"
                  </Typography>
                </Box>

                <Box sx={{ textAlign: "center" }}>
                  <Box
                    component="img"
                    src="https://images.pexels.com/photos/289737/pexels-photo-289737.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&dpr=1"
                    alt="School Building"
                    sx={{
                      width: "100%",
                      maxWidth: 350,
                      height: 250,
                      objectFit: "cover",
                      borderRadius: 3,
                      boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
                    }}
                  />
                </Box>
              </Box>
            </Container>
          </Paper>

          <Container maxWidth="lg">
            {/* VISION, MISSION & VALUES */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                gap: 3,
                mb: 8,
              }}
            >
              {/* Vision */}
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  p: 2,
                }}
              >
                <CardContent sx={{ textAlign: "center" }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "50%",
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      display: "inline-flex",
                      mb: 2,
                    }}
                  >
                    <Visibility sx={{ fontSize: 40, color: "info.main" }} />
                  </Box>
                  <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
                    Our Vision
                  </Typography>
                  <Typography variant="body1" sx={{ color: "text.secondary" }}>
                    {school.vision}
                  </Typography>
                </CardContent>
              </Card>

              {/* Mission */}
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  p: 2,
                }}
              >
                <CardContent sx={{ textAlign: "center" }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "50%",
                      bgcolor: alpha(theme.palette.secondary.main, 0.1),
                      display: "inline-flex",
                      mb: 2,
                    }}
                  >
                    <Flag sx={{ fontSize: 40, color: "secondary.main" }} />
                  </Box>
                  <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
                    Our Mission
                  </Typography>
                  <Typography variant="body1" sx={{ color: "text.secondary" }}>
                    {school.mission}
                  </Typography>
                </CardContent>
              </Card>

              {/* Values */}
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  p: 2,
                }}
              >
                <CardContent sx={{ textAlign: "center" }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "50%",
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      display: "inline-flex",
                      mb: 2,
                    }}
                  >
                    <EmojiEvents sx={{ fontSize: 40, color: "warning.main" }} />
                  </Box>
                  <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
                    Our Values
                  </Typography>
                  <Typography variant="body1" sx={{ color: "text.secondary" }}>
                    Excellence, Integrity, Innovation, and Community - the
                    pillars that guide our educational approach.
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* CONTACT INFO */}
            <Box sx={{ mb: 8 }}>
              <Typography
                variant="h4"
                sx={{ mb: 4, textAlign: "center", fontWeight: 700 }}
              >
                Contact Information
              </Typography>
              <Paper
                sx={{ p: 0, overflow: "hidden", bgcolor: "background.paper" }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  }}
                >
                  <Box sx={{ p: 5 }}>
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
                      Head Office
                    </Typography>
                    <List>
                      <ListItem disableGutters>
                        <ListItemIcon>
                          <LocationOn color="secondary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Address"
                          secondary={`${school.headOfficeAddress.streetLine1}, ${school.headOfficeAddress.city}, ${school.headOfficeAddress.country}`}
                        />
                      </ListItem>
                      <ListItem disableGutters>
                        <ListItemIcon>
                          <Email color="secondary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Email"
                          secondary={school.contactEmail}
                        />
                      </ListItem>
                      <ListItem disableGutters>
                        <ListItemIcon>
                          <Phone color="secondary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Phone"
                          secondary={school.contactPhone}
                        />
                      </ListItem>
                    </List>
                  </Box>

                  <Box
                    component="img"
                    src="https://images.pexels.com/photos/159844/cellular-education-classroom-159844.jpeg?auto=compress&cs=tinysrgb&w=500&h=300&dpr=1"
                    alt="Classroom"
                    sx={{
                      width: "100%",
                      height: "100%",
                      minHeight: 300,
                      objectFit: "cover",
                    }}
                  />
                </Box>
              </Paper>
            </Box>

            {/* CAMPUSES */}
            <Box sx={{ mb: 8 }}>
              <Typography variant="h4" sx={{ mb: 4, fontWeight: 700 }}>
                Our Campuses
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 3,
                }}
              >
                {activeBranches.map((branch) => (
                  <Card
                    key={branch.branchCode}
                    sx={{
                      height: "100%",
                      cursor: "pointer",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-5px)",
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0 10px 30px rgba(0,0,0,0.5)"
                            : "0 10px 30px rgba(0,0,0,0.1)",
                        borderColor: "secondary.main",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Stack
                        direction="row"
                        sx={{
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          mb: 2,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={2}
                          sx={{ alignItems: "center" }}
                        >
                          <Box
                            sx={{
                              p: 1,
                              borderRadius: theme.appRadius?.sm || 1,
                              bgcolor: alpha(theme.palette.secondary.main, 0.1),
                              display: "flex",
                            }}
                          >
                            <CampaignOutlined color="secondary" />
                          </Box>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                              {branch.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary" }}
                            >
                              Code: {branch.branchCode}
                            </Typography>
                          </Box>
                        </Stack>
                        <Chip label="Active" color="success" size="small" />
                      </Stack>

                      <Divider sx={{ my: 2 }} />

                      <Stack spacing={2}>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{ color: "text.secondary", mb: 0.5 }}
                          >
                            Principal
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{ alignItems: "center" }}
                          >
                            <Person
                              fontSize="small"
                              sx={{ color: "action.active" }}
                            />
                            <Typography variant="body2">
                              {branch.principalName}
                            </Typography>
                          </Stack>
                        </Box>

                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{ color: "text.secondary", mb: 0.5 }}
                          >
                            Address
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "text.primary" }}
                          >
                            {`${branch.address.streetLine1}, ${branch.address.city}, ${branch.address.country}`}
                          </Typography>
                        </Box>

                        <Stack spacing={1}>
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{ alignItems: "center" }}
                          >
                            <Email
                              fontSize="small"
                              sx={{ color: "action.active" }}
                            />
                            <Typography variant="body2">
                              {branch.contactEmail}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{ alignItems: "center" }}
                          >
                            <Phone
                              fontSize="small"
                              sx={{ color: "action.active" }}
                            />
                            <Typography variant="body2">
                              {branch.contactPhone}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>

            {/* WHY CHOOSE US */}
            <Box sx={{ mb: 6 }}>
              <Paper sx={{ p: { xs: 3, md: 5 }, bgcolor: "background.paper" }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "7fr 5fr" },
                    gap: 4,
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
                      Why Choose {school.name}?
                    </Typography>
                    <List>
                      {[
                        {
                          title: "Experienced Faculty",
                          desc: "Qualified teachers committed to success",
                        },
                        {
                          title: "Modern Facilities",
                          desc: "State-of-the-art labs and resources",
                        },
                        {
                          title: "Holistic Development",
                          desc: "Focus on academic and emotional growth",
                        },
                        {
                          title: "Small Class Sizes",
                          desc: "Personalized attention for every student",
                        },
                      ].map((item, index) => (
                        <ListItem key={index} sx={{ py: 1.5, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <CheckCircle
                              color="success"
                              sx={{ fontSize: 28 }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {item.title}
                              </Typography>
                            }
                            secondary={item.desc}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  <Box
                    component="img"
                    src="https://images.pexels.com/photos/8613089/pexels-photo-8613089.jpeg?auto=compress&cs=tinysrgb&w=500&h=350&dpr=1"
                    alt="Students Learning"
                    sx={{
                      width: "100%",
                      height: 350,
                      objectFit: "cover",
                      borderRadius: theme.appRadius?.lg || 2,
                      boxShadow: 4,
                    }}
                  />
                </Box>
              </Paper>
            </Box>
          </Container>
        </>
      )}
    </Box>
  );
};

export default HomePage;
